import React from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { Icon, Spacing, Text } from '@reservamos/elements';
import discount from '../../images/doters/discount.svg';
import earned from '../../images/doters/gain.svg';
import grayDoters from '../../images/doters/graydoters.svg';
import './DotersPointsSummary.scss';

/**
 * DotersPointsSummary component displays a summary of doters points used and earned.
 * It shows the points used and earned with appropriate icons and labels.
 * @param {Object} props - The component props.
 * @param {string} props.dotersUsed - The number of doters points used.
 * @param {string} props.dotersEarned - The number of doters points earned.
 * @param {string} [props.status='pending'] - The status of the doters
 * @param {boolean} [props.isFirstPassengerNotAccountHolder=false] - Whether the first passenger is not the account holder.
 * @returns {JSX.Element} The rendered component.
 */
const DotersPointsSummary = ({
  dotersUsed,
  dotersEarned,
  status = 'pending',
  isFirstPassengerNotAccountHolder,
}) => {
  const { t } = useTranslation('loyalty');
  const isPending = status === 'pending';
  const isRefunded = status === 'refunded';

  const usedText = isPending ? t('doters_to_be_used') : t('doters_Used');
  const earnedText = isPending ? t('doters_to_be_earn') : t('doters_Earned');
  const refundedText = t('doters_Refunded');

  if (!dotersUsed && !dotersEarned && !isFirstPassengerNotAccountHolder) {
    return null;
  }

  return (
    <Spacing vertical size="XS">
      <div className="doters-points-summary">
        {!isFirstPassengerNotAccountHolder && <div className="doters-points-summary-image" />}
        <div className="doters-points-summary-header">
          <Text weight="semibold" mobileSize="S" color="white">
            {isFirstPassengerNotAccountHolder
              ? t('summary_no_accumulate_title')
              : t('summary_title', { context: status })}
          </Text>
        </div>
        <div className="doters-points-summary-wrapper">
          {isFirstPassengerNotAccountHolder ? (
            <Spacing alignItems="center">
              <Icon type={grayDoters} size="L" />
              <Text size="S">
                <Trans t={t} i18nKey="summary_first_passenger_message" components={[<b />]} />
              </Text>
            </Spacing>
          ) : (
            <Spacing alignItems="center" justifyContent="center" flexGrow size="XS">
              {!!dotersUsed && !isRefunded && (
                <Spacing alignItems="center" justifyContent="center" size="XS" isResponsive>
                  <Icon type={discount} size="L" />
                  <Text size="XL" color="grayLight" weight="semibold">
                    {dotersUsed}
                  </Text>
                  <Text color="grayLight" size="S">
                    {usedText}
                  </Text>
                </Spacing>
              )}
              {!!dotersEarned && !isRefunded && (
                <Spacing alignItems="center" justifyContent="center" size="XS" isResponsive>
                  <Icon type={earned} size="XL" />
                  <Text size="XL" color="loyaltyLight" weight="semibold">
                    {dotersEarned}
                  </Text>
                  <Text color="loyaltyLight" size="S">
                    {earnedText}
                  </Text>
                </Spacing>
              )}
              {isRefunded && (
                <Spacing alignItems="center" justifyContent="center" size="XS" isResponsive>
                  <Icon type={earned} size="XL" />
                  <Text size="XL" color="loyaltyLight" weight="semibold">
                    {dotersUsed}
                  </Text>
                  <Text color="loyaltyLight" size="S">
                    {refundedText}
                  </Text>
                </Spacing>
              )}
            </Spacing>
          )}
        </div>
      </div>
      {isRefunded && (
        <Text size="S" mobileSize="XS" color="grayLight">
          {t('summary_refunded')}
        </Text>
      )}
    </Spacing>
  );
};

DotersPointsSummary.propTypes = {
  dotersUsed: PropTypes.string,
  dotersEarned: PropTypes.string,
  status: PropTypes.oneOf(['pending', 'refunded', 'completed']),
  isFirstPassengerNotAccountHolder: PropTypes.bool,
};

DotersPointsSummary.defaultProps = {
  dotersUsed: '',
  dotersEarned: '',
  isFirstPassengerNotAccountHolder: false,
};

export default DotersPointsSummary;
